import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  useTheme,
  Button,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
} from "@mui/material";
import {
  FileDownload as FileDownloadIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";
import AccessMatrixTable from "./components/AccessMatrixTable";
import { useAccessMatrixData } from "./hooks/useAccessMatrixData";
import { filterUsers } from "./utils/dataTransformers";
import { exportAccessMatrixToExcel } from "./utils/exportToExcel";
import { useQuery } from "@apollo/client";
import { GET_SYSTEMS } from "../../../../apis/settingsApis/access-apis/accessApis";
import { SystemKey, IntegrationType } from "../../../../utils/enums";
const AccessMatrixTab = () => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedSystemName, setSelectedSystemName] = useState(null);
  const [selectedIntegrationType, setSelectedIntegrationType] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState(null);

  const [systemNames, setSystemNames] = useState([]);
  const [integrationTypes, setIntegrationTypes] = useState([]);

  // Build filter object for backend
  const filterObject = useMemo(() => {
    const filter = {};
    if (selectedSystemName) {
      filter.label = selectedSystemName;
    }
    if (selectedIntegrationType) {
      filter.integration_type = [selectedIntegrationType];
    }
    return filter;
  }, [selectedSystemName, selectedIntegrationType]);

  // Fetch all systems for filter dropdown (unfiltered)
  const { data: allSystemsData } = useQuery(GET_SYSTEMS, {
    variables: {
      page: 1,
      limit: 1000,
      filter: {},
    },
    fetchPolicy: "cache-and-network",
  });

  // Fetch and transform data
  const { uniqueSystems, userAccessMatrix, roles, loading, systems } = useAccessMatrixData(filterObject);

  // Extract filter options from all systems (for dropdown)
  useEffect(() => {
    // Exclude Zoho People only if it's not manual (allow manual Zoho People)
    const allSystems = (allSystemsData?.getSystems?.data || []).filter((s) => {
      const isZohoPeople = s.key?.toLowerCase() === SystemKey?.ZOHO_PEOPLE;
      return !isZohoPeople || (isZohoPeople && (s.integration_type || '').toUpperCase() === IntegrationType.MANUAL);
    });
    if (allSystems?.length > 0) {
      // Get unique system names (labels) from all systems
      const uniqueSystemNames = [
        ...new Set(
          allSystems?.map((system) => system?.label).filter(Boolean)
        ),
      ].sort();
      setSystemNames(uniqueSystemNames);

      // Extract integration types from all systems
      const uniqueIntegrationTypes = [
        ...new Set(
          allSystems?.map((system) => {
            return system?.integration_type || null;
          }).filter(Boolean)
        ),
      ].sort();
      setIntegrationTypes(uniqueIntegrationTypes);
    }
  }, [allSystemsData]);

  // Filter users based on search and role (system name filtering is done at backend)
  const filteredUsers = useMemo(
    () => filterUsers(userAccessMatrix, searchQuery, selectedRole),
    [userAccessMatrix, searchQuery, selectedRole]
  );

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedRole(null);
    setSelectedSystemName(null);
    setSelectedIntegrationType(null);
  };

  const removeFilter = (filterType) => {
    switch (filterType) {
      case 'system':
        setSelectedSystemName(null);
        break;
      case 'source':
        setSelectedIntegrationType(null);
        break;
      default:
        break;
    }
  };

  const activeFiltersCount = [
    selectedSystemName,
    selectedIntegrationType,
  ].filter(Boolean).length;

  const hasActiveFilters = searchQuery || selectedSystemName || selectedIntegrationType;

  const handleExport = () => {
    const result = exportAccessMatrixToExcel(filteredUsers, uniqueSystems);

    if (result.success) {
      setSnackbarInfo({
        message: "Access matrix exported successfully!",
        vertical: "top",
        horizontal: "right",
      });
    } else {
      setSnackbarInfo({
        message: "Failed to export access matrix",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const totalUsers = filteredUsers.length;

  return (
    <>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
      <Box sx={{ padding: { xs: 2 }, minHeight: "100vh" }}>
        <Card
          sx={{
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid #E8EAED",
          }}
        >
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  variant="h5"
                  fontWeight="600"
                  color={theme.customColors.text.strong}
                >
                  Access Matrix
                </Typography>
              </Box>
            }
            action={
              <Button
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
                disabled={totalUsers === 0}
                sx={{
                  bgcolor: theme.customColors.button.primary,
                  color: theme.customColors.button.text,
                  fontWeight: 500,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "14px",
                  px: 2,
                  py: 1,
                  "&:hover": { bgcolor: theme.palette._blue.dark },
                  "&:disabled": {
                    bgcolor: theme.customColors.background.light,
                    color: theme.customColors.text.disabled,
                  },
                }}
              >
                Export Matrix
              </Button>
            }
            sx={{
              borderBottom: "1px solid #ddd",
              background: "#F7F7FC",
              ".MuiCardHeader-title": {
                fontSize: "16px",
                fontWeight: "bold",
              },
              padding: "14px",
            }}
          />

          <CardContent sx={{ p: 0 }}>
            {/* Modern Filter Section */}
            <Box sx={{ p: 2, pb: 1.5, backgroundColor: theme.customColors.background.main }}>
              {/* Search and Filter Button Row */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery('')}
                          edge="end"
                        >
                          <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: 'white',
                    },
                  }}
                />

                <Button
                  variant={activeFiltersCount > 0 ? "contained" : "outlined"}
                  startIcon={<FilterListIcon />}
                  endIcon={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s',
                      }}
                    >
                      <ExpandMoreIcon sx={{ fontSize: 18 }} />
                    </Box>
                  }
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    minWidth: 140,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    ...(activeFiltersCount > 0 && {
                      bgcolor: "#3B82F6",
                      color: "#fff",
                      '&:hover': {
                        bgcolor: "#2563EB",
                      },
                    }),
                    ...(activeFiltersCount === 0 && {
                      borderColor: "#3B82F6",
                      color: "#3B82F6",
                      '&:hover': {
                        borderColor: "#2563EB",
                        bgcolor: 'rgba(59, 130, 246, 0.08)',
                      },
                    }),
                  }}
                >
                  Filters
                  {activeFiltersCount > 0 && (
                    <Chip
                      label={activeFiltersCount}
                      size="small"
                      sx={{
                        ml: 1,
                        height: 20,
                        minWidth: 20,
                        bgcolor: 'white',
                        color: "#3B82F6",
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        '& .MuiChip-label': {
                          px: 0.75,
                        },
                      }}
                    />
                  )}
                </Button>
              </Box>

              {/* Collapsible Filter Panel */}
              <Collapse in={showFilters}>
                <Box
                  sx={{
                    p: 2.5,
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <FormControl fullWidth size="small">
                      <InputLabel>System Name</InputLabel>
                      <Select
                        value={selectedSystemName || ''}
                        onChange={(e) => setSelectedSystemName(e.target.value || null)}
                        label="System Name"
                        sx={{
                          borderRadius: '8px',
                          backgroundColor: 'white',
                        }}
                      >
                        <MenuItem value="">All Systems</MenuItem>
                        {systemNames.map((systemName) => (
                          <MenuItem key={systemName} value={systemName}>
                            {systemName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                      <InputLabel>Source</InputLabel>
                      <Select
                        value={selectedIntegrationType || ''}
                        onChange={(e) => setSelectedIntegrationType(e.target.value || null)}
                        label="Source"
                        sx={{
                          borderRadius: '8px',
                          backgroundColor: 'white',
                        }}
                      >
                        <MenuItem value="">All Sources</MenuItem>
                        <MenuItem value="MANUAL">Manual</MenuItem>
                        <MenuItem value="AUTOMATED">Automated</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {hasActiveFilters && (
                    <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        onClick={clearFilters}
                        startIcon={<ClearIcon />}
                        sx={{
                          textTransform: 'none',
                          color: "#3B82F6",
                          fontWeight: 500,
                          '&:hover': {
                            bgcolor: 'rgba(59, 130, 246, 0.08)',
                          },
                        }}
                      >
                        Clear all filters
                      </Button>

                      {/* Active Filters Chips */}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {selectedSystemName && (
                          <Chip
                            label={`System: ${selectedSystemName}`}
                            onDelete={() => removeFilter('system')}
                            size="small"
                            sx={{
                              backgroundColor: "#3B82F6",
                              color: "#fff",
                              border: "2px solid #3B82F6",
                              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
                              fontWeight: 600,
                              fontSize: "0.85rem",
                              height: "32px",
                              borderRadius: "12px",
                              '& .MuiChip-deleteIcon': {
                                color: "#fff",
                                '&:hover': {
                                  color: "#E5E7EB",
                                },
                              },
                            }}
                          />
                        )}
                        {selectedIntegrationType && (
                          <Chip
                            label={`Source: ${selectedIntegrationType === 'MANUAL' ? 'Manual' : 'Automated'}`}
                            onDelete={() => removeFilter('source')}
                            size="small"
                            sx={{
                              backgroundColor: "#3B82F6",
                              color: "#fff",
                              border: "2px solid #3B82F6",
                              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
                              fontWeight: 600,
                              fontSize: "0.85rem",
                              height: "32px",
                              borderRadius: "12px",
                              '& .MuiChip-deleteIcon': {
                                color: "#fff",
                                '&:hover': {
                                  color: "#E5E7EB",
                                },
                              },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Collapse>

              {/* Results Count */}
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                {totalUsers} user{totalUsers !== 1 ? "s" : ""} found
              </Typography>
            </Box>

            <Divider sx={{ borderColor: theme.customColors.border.main }} />

            {/* Access Matrix Table */}
            {filteredUsers.length === 0 && !loading ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="body1" color="text.secondary">
                  {searchQuery
                    ? "No users found matching your search"
                    : "No access data available"}
                </Typography>
              </Box>
            ) : (
              <AccessMatrixTable
                filteredUsers={filteredUsers}
                uniqueSystems={uniqueSystems}
                loading={loading}
              />
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default AccessMatrixTab;
